import {match} from 'ts-pattern';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {type FiltersType} from '../types/FiltersType';
import {VehicleCategoryFilter} from '../types/VehicleCategoryFilter';

type VanFilterName = 'vehicleCategory' | 'vehicleLengthCategory' | 'vehicleHeightCategory';

type OmitHiddenVanFiltersParams = {
  filters: FiltersType;
  filterPresetId: string | Nullish;
  /** Filters stored in the applied preset. */
  presetFilters: FiltersType | Nullish;
  isVanFiltersEnabled: boolean;
};

export type ClassifiedsRequestInput = {
  filters: FiltersType;
  filterPresetId: string | Nullish;
};

const VAN_DIMENSION_FILTER_NAMES: VanFilterName[] = [
  'vehicleLengthCategory',
  'vehicleHeightCategory',
];

/**
 * Van filters the classifieds request must not use: all of them with `sourcing_van_filters` off,
 * the body length / height classes for a passenger-car search.
 */
const getHiddenVanFilterNames = (
  vehicleCategory: VehicleCategoryFilter | Nullish,
  isVanFiltersEnabled: boolean
): VanFilterName[] =>
  match({isVanFiltersEnabled, isCarSearch: vehicleCategory === VehicleCategoryFilter.CAR})
    .with({isVanFiltersEnabled: false}, (): VanFilterName[] => [
      'vehicleCategory',
      ...VAN_DIMENSION_FILTER_NAMES,
    ])
    .with({isCarSearch: true}, () => VAN_DIMENSION_FILTER_NAMES)
    .otherwise(() => []);

/**
 * Hidden van filters coming from storage or a saved preset must not filter the classifieds
 * request, since the user can neither see nor remove them.
 *
 * Clearing them in the body is not enough: when `filterPresetId` is sent, the API rebuilds the
 * filter from the stored preset and would put them back. So the preset id is dropped whenever the
 * applied preset itself carries a hidden van filter – regardless of what the (possibly debounced
 * or already edited) filter state holds. Applying a preset already hydrates its filters into the
 * state, so the request is built from that state instead.
 */
export const omitHiddenVanFilters = ({
  filters,
  filterPresetId,
  presetFilters,
  isVanFiltersEnabled,
}: OmitHiddenVanFiltersParams): ClassifiedsRequestInput => {
  const hiddenFilterNames = getHiddenVanFilterNames(filters.vehicleCategory, isVanFiltersEnabled);
  const hiddenPresetFilterNames = getHiddenVanFilterNames(
    presetFilters?.vehicleCategory,
    isVanFiltersEnabled
  );
  const hasHiddenPresetFilter = [...hiddenFilterNames, ...hiddenPresetFilterNames].some((name) =>
    isNotNilOrEmpty(presetFilters?.[name])
  );

  const isHidden = (name: VanFilterName) => hiddenFilterNames.includes(name);

  return {
    filters: {
      ...filters,
      vehicleCategory: isHidden('vehicleCategory') ? null : filters.vehicleCategory,
      vehicleLengthCategory: isHidden('vehicleLengthCategory')
        ? undefined
        : filters.vehicleLengthCategory,
      vehicleHeightCategory: isHidden('vehicleHeightCategory')
        ? undefined
        : filters.vehicleHeightCategory,
    },
    filterPresetId: hasHiddenPresetFilter ? null : filterPresetId,
  };
};
